import type {loader as rootLoader} from './root'
import {SelectedAccountContactsProvider} from '@shm/shared/models/contacts'
import {useLocation, useNavigate, useNavigation, useRouteLoaderData} from '@remix-run/react'
import {UnpackedHypermediaId} from '@seed-hypermedia/client'
import {NavRoute, OptimizedImageSize, routeToHref, UniversalAppProvider} from '@shm/shared'
import {DAEMON_FILE_URL, SEED_ASSET_HOST, SITE_BASE_URL} from '@shm/shared/constants'
import {languagePacks} from '@shm/shared/language-packs'
import {registerQueryClient} from '@shm/shared/models/query-client'
import {ReadOnlyViewerComponent, ReadOnlyViewerProvider} from '@shm/shared/readonly-viewer-context'
import {getDocumentTitle} from '@shm/shared/content'
import {queryAccount, queryResource} from '@shm/shared/models/queries'
import {queryKeys} from '@shm/shared/models/query-keys'
import {unpackHmId} from '@shm/shared/utils/entity-id-url'
import {recordRecentRoute} from './local-db-recents'
import {defaultRoute, getRecentsRouteEntityUrl} from '@shm/shared/routes'
import {
  isHttpUrl,
  useNavRoute,
  NavAction,
  NavContextProvider,
  NavState,
  navStateReducer,
} from '@shm/shared/utils/navigation'
import {AssistantPanelProvider} from './assistant-panel-state'
import {SiteContextPublisher} from './site-context-bridge'
import {WebDocumentMaintenance} from './document-maintenance'
import {WebAssistantHost} from './web-assistant-host'
import {WebDomainHost} from './components/web-domain-host'
import {hypermediaUrlToRoute} from '@shm/shared/utils/url-to-route'
import type {StateStream} from '@shm/shared/utils/stream'
import {writeableStateStream} from '@shm/shared/utils/stream'
import {useCopyHmLink} from '@shm/ui/use-copy-hm-link'
import {Spinner} from '@shm/ui/spinner'
import {toast, Toaster} from '@shm/ui/toast'
import {NotificationInboxProvider} from '@shm/ui/activity-panel'
import {TooltipProvider} from '@shm/ui/tooltip'
import {DehydratedState, hydrate, QueryClient, QueryClientProvider, useQueryClient} from '@tanstack/react-query'
import {createContext, useContext, useEffect, useMemo, useState} from 'react'
import {keyPairStore} from './auth'
import {webUniversalClient} from './universal-client'
import {WebNotificationInboxClientOnly} from './web-notification-inbox'
import {isPerfEnabled, markNavEnd, markNavStart} from './web-perf-marks'
import {installWebDocumentPages} from './web-document-pages'

installWebDocumentPages()

function WebRecentVisitRecorder() {
  const route = useNavRoute()
  const url = getRecentsRouteEntityUrl(route)
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!url) return
    const id = unpackHmId(url)
    if (!id) return
    let cancelled = false
    const visitedRoute: NavRoute = id.path?.[0] === ':profile' ? {key: 'profile', id} : {key: 'document', id}
    async function record() {
      let name = id!.uid
      try {
        if (visitedRoute.key === 'profile') {
          const account = await queryClient.fetchQuery(queryAccount(webUniversalClient, id!.uid))
          if (account) name = account.metadata?.name || name
        } else {
          const resource = await queryClient.fetchQuery(queryResource(webUniversalClient, id!))
          if (resource?.type === 'document') name = getDocumentTitle(resource.document) || name
        }
      } catch {
        // Keep the visit even when its display metadata cannot be loaded.
      }
      if (cancelled) return
      const recent = await recordRecentRoute(visitedRoute, name)
      if (recent) {
        await Promise.all([
          queryClient.invalidateQueries([queryKeys.RECENTS]),
          queryClient.invalidateQueries([queryKeys.SEARCH, 'inlineMentions']),
        ])
      }
    }
    void record()
    return () => {
      cancelled = true
    }
  }, [url, queryClient])
  return null
}

function getSelectedIdentity(): string | null {
  const kp = keyPairStore.get()
  if (!kp) return null
  return kp.delegatedAccountUid ?? kp.id
}

function getSigningIdentity(): string | null {
  const kp = keyPairStore.get()
  if (!kp) return null
  return kp.id
}

const selectedIdentity: StateStream<string | null> = {
  get: getSelectedIdentity,
  subscribe: (handler) => {
    const wrapped = () => handler(getSelectedIdentity())
    return keyPairStore.subscribe(wrapped)
  },
}

const signingIdentity: StateStream<string | null> = {
  get: getSigningIdentity,
  subscribe: (handler) => {
    const wrapped = () => handler(getSigningIdentity())
    return keyPairStore.subscribe(wrapped)
  },
}

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  })
}

// Browser singleton - only created once on client
let browserQueryClient: QueryClient | null = null

function getQueryClient() {
  // Server: always create new client for each request (avoid data leakage)
  if (typeof window === 'undefined') {
    return createQueryClient()
  }
  // Browser: use singleton
  if (!browserQueryClient) {
    browserQueryClient = createQueryClient()
    registerQueryClient(browserQueryClient)
    // Dev-only: expose for DevTools console (e.g. forcing transient resource errors).
    if (import.meta.env?.DEV) {
      ;(window as any).__qc = browserQueryClient
    }
  }
  return browserQueryClient
}

type ThemeContextType = {
  theme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark') => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

function useClientReadOnlyViewer(): ReadOnlyViewerComponent | undefined {
  const [Component, setComponent] = useState<ReadOnlyViewerComponent | undefined>(undefined)
  useEffect(() => {
    import('@shm/editor/readonly-viewer').then((mod) => {
      setComponent(() => mod.ReadOnlyViewer)
    })
  }, [])
  return Component
}

export const Providers = (props: {children: any}) => {
  const [client] = useState(getQueryClient)
  const ReadOnlyViewer = useClientReadOnlyViewer()
  return (
    <ThemeProvider>
      <QueryClientProvider client={client}>
        <ReadOnlyViewerProvider value={ReadOnlyViewer}>
          {/* Above the outlet on purpose: the assistant panel and its open/session state must
              outlive the per-page WebSiteProvider, or every route change would remount the panel.
              The page contexts the panel needs reach it through the site-context bridge. */}
          <AssistantPanelProvider>
            <WebAssistantHost>
              <WebDocumentMaintenance>{props.children}</WebDocumentMaintenance>
              <WebDomainHost />
            </WebAssistantHost>
          </AssistantPanelProvider>
        </ReadOnlyViewerProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

function useNavigationLoading() {
  const navigation = useNavigation()
  const isNavigating = navigation.state === 'loading'
  const [showLoading, setShowLoading] = useState(false)

  useEffect(() => {
    if (!isNavigating) {
      if (isPerfEnabled()) markNavEnd()
      setShowLoading(false)
      return
    }
    const timeout = setTimeout(() => setShowLoading(true), 400)
    return () => clearTimeout(timeout)
  }, [isNavigating])

  return showLoading
}

const NavigationLoadingContext = createContext(false)

export function useIsNavigationLoading() {
  return useContext(NavigationLoadingContext)
}

function NavigationLoadingProvider({children}: {children: React.ReactNode}) {
  const showLoading = useNavigationLoading()
  return (
    <NavigationLoadingContext.Provider value={showLoading}>
      {children}
      {showLoading && (
        <div className="fixed right-4 bottom-16 z-50 sm:bottom-4">
          <Spinner size="small" />
        </div>
      )}
    </NavigationLoadingContext.Provider>
  )
}

export function NavigationLoadingContent({children, className}: {children: React.ReactNode; className?: string}) {
  const isLoading = useIsNavigationLoading()
  return (
    <div
      className={`transition-opacity duration-200 ${isLoading ? 'pointer-events-none opacity-50' : ''} ${
        className || ''
      }`}
    >
      {children}
    </div>
  )
}

export function ThemeProvider({children}: {children: React.ReactNode}) {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Check system preference on initial load
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
  })

  // Update document class when theme changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }
  }, [theme])

  // Listen for system theme changes
  // @ts-expect-error
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        setTheme(e.matches ? 'dark' : 'light')
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  return (
    <ThemeContext.Provider value={{theme, setTheme, toggleTheme}}>
      <TooltipProvider>
        <NavigationLoadingProvider>{children}</NavigationLoadingProvider>
        <div className="fixed right-0 bottom-0 z-50 h-auto w-full">
          <Toaster theme={theme} />
        </div>
      </TooltipProvider>
    </ThemeContext.Provider>
  )
}

export function getOptimizedImageUrl(cid: string, size?: OptimizedImageSize) {
  let url = SEED_ASSET_HOST || ''
  url += `/hm/api/image/${cid}`
  if (size) url += `?size=${size}`
  return url
}

// Experiments enabled for every web visitor. Web has no per-user experiments
// toggle, so building-block features that desktop hides behind Developer Mode
// are simply on here (see the blob editor at /hm/inspect/ipfs/new).
const WEB_EXPERIMENTS = {developerMode: true} as const

export function WebSiteProvider(props: {
  originHomeId?: UnpackedHypermediaId
  children: React.ReactNode
  siteHost?: string
  origin?: string
  prefersLanguages?: (keyof typeof languagePacks)[]
  dehydratedState?: DehydratedState
  initialRoute?: NavRoute
}) {
  const navigate = useNavigate()
  const rootData = useRouteLoaderData<typeof rootLoader>('root')
  const client = useQueryClient()

  // Hydrate synchronously so SSR hooks can access prefetched data
  if (props.dehydratedState) {
    hydrate(client, props.dehydratedState)
  }

  const languagePack = useMemo(() => {
    const language = props.prefersLanguages?.[0]
    if (language) {
      return languagePacks[language]
    }
    return undefined
  }, [props.prefersLanguages])

  // Create navigation context with initial route from URL
  const navigation = useMemo(() => {
    const initialNav: NavState = {
      sidebarLocked: false,
      routes: [props.initialRoute ?? defaultRoute],
      routeIndex: 0,
      lastAction: 'replace',
    }
    const [updateNavState, navState] = writeableStateStream(initialNav)

    return {
      dispatch(action: NavAction) {
        const prevState = navState.get()
        const newState = navStateReducer(prevState, action)
        if (prevState !== newState) {
          updateNavState(newState)
        }
      },
      state: navState,
    }
  }, [])

  // Track whether navigation was initiated by openRoute (vs browser back/forward)
  const isInternalNav = useMemo(() => ({current: false}), [])
  const routerLocation = useLocation()
  const {pathname: routerPathname, search: routerSearch} = routerLocation
  const copyHmLink = useCopyHmLink()

  // Sync browser back/forward into NavContext
  // When Remix re-runs the loader on popstate, initialRoute updates but NavContext
  // doesn't. Detect location changes not initiated by openRoute and sync the route.
  useEffect(() => {
    if (isInternalNav.current) {
      isInternalNav.current = false
      return
    }
    // Query-only transitions can skip the loader. Read the live URL so Back
    // and Forward restore the reply target instead of the stale loader route.
    const hmPath = routerPathname.startsWith('/hm/')
      ? routerPathname
      : props.originHomeId
        ? `/hm/${props.originHomeId.uid}${routerPathname === '/' ? '' : routerPathname}`
        : null
    const route = hmPath ? hypermediaUrlToRoute(`${props.origin}${hmPath}${routerSearch}${routerLocation.hash}`) : null
    const params = new URLSearchParams(routerSearch)
    const panelParam = params.get('panel')
    if (route?.key === 'comments' && /^(comments?|discussions)(\/|$)/.test(panelParam || '')) {
      params.delete('panel')
      const search = params.toString()
      navigate(`${routerPathname}${search ? `?${search}` : ''}${routerLocation.hash}`, {replace: true})
    }
    if (route || props.initialRoute) {
      navigation.dispatch({type: 'replace', route: route || props.initialRoute!})
    }
  }, [routerPathname, routerSearch])

  return (
    <UniversalAppProvider
      isGateway={rootData?.isGateway ?? false}
      origin={props.origin}
      originHomeId={props.originHomeId}
      // The blob/schema building-block features (desktop gates these behind a
      // Developer Mode toggle) are enabled by default on web — there is no
      // per-user toggle here, so the experimental surfaces are always on.
      experiments={WEB_EXPERIMENTS}
      languagePack={languagePack}
      getOptimizedImageUrl={getOptimizedImageUrl}
      ipfsFileUrl={DAEMON_FILE_URL}
      openUrl={(url, newWindow) => {
        if (!url) return
        const route = hypermediaUrlToRoute(url)
        if (route) {
          const href = routeToHref(route, {originHomeId: props.originHomeId}) || undefined
          if (href !== undefined) {
            if (newWindow) {
              window.open(href, '_blank')
            } else {
              isInternalNav.current = true
              navigation.dispatch({type: 'push', route})
              navigate(href)
            }
            return
          }
        }
        if (isHttpUrl(url)) {
          window.open(url, newWindow ? '_blank' : '_self')
          return
        }
        console.warn(`[openUrl] Failed to resolve route for "${url}"`)
      }}
      universalClient={webUniversalClient}
      selectedIdentity={selectedIdentity}
      signingIdentity={signingIdentity}
      openRoute={(route: NavRoute, replace?: boolean) => {
        if (isPerfEnabled()) markNavStart()
        // Update navigation state
        if (replace) {
          navigation.dispatch({type: 'replace', route})
        } else {
          navigation.dispatch({type: 'push', route})
        }

        // Handle browser navigation
        const href =
          routeToHref(route, {
            originHomeId: props.originHomeId,
          }) || undefined
        if (href !== undefined) {
          if (
            // this is a HACK to redirect to the home page when the user is on the home page of seed.hyper.media because we have an external landing page.
            window.location.host == 'seed.hyper.media' &&
            typeof href == 'string' &&
            (href == '/' || href == '')
          ) {
            console.log('redirecting to seed.hyper.media')
            window.location.assign('https://seed.hyper.media')
          } else {
            isInternalNav.current = true
            navigate(href, {
              replace,
            })
          }
        } else {
          toast.error('Failed to open route')
        }
      }}
      onCopyReference={async (hmId: UnpackedHypermediaId) => {
        // Keep the link pointing at this deployment's origin (passed as the
        // gateway URL) so gateway-format links (/hm/<uid>/...) resolve here.
        await copyHmLink({
          id: {...hmId, hostname: null},
          gatewayUrl: SITE_BASE_URL,
        })
      }}
    >
      <SelectedAccountContactsProvider>
        <NavContextProvider value={navigation}>
          <SiteContextPublisher />
          <WebRecentVisitRecorder />
          <NotificationInboxProvider value={WebNotificationInboxClientOnly}>{props.children}</NotificationInboxProvider>
        </NavContextProvider>
      </SelectedAccountContactsProvider>
    </UniversalAppProvider>
  )
}
